<template>
  <div class="app-wrapper">
    <div class="mainContainer">
      <PageHeader
        title="Test Manager"
        subtitle="Create and manage your assessment tests"
        icon="bi-clipboard-check"
        color="blue"
      >
        <template #actions>
          <button title="Create a new Test" class="btn-new-test" @click="toggleTestForm">
            <i class="bi bi-plus-circle"></i>
            <span>New Test</span>
          </button>
          <FileUploader
            title="Import a new file - Respect the WASPL JSON format!"
            @import-test="importTestToTable"
            @close="closeImportForm" />
        </template>
      </PageHeader>

      <!-- Statistics Indicators -->
      <div class="statistics-container">
        <div class="stat-card stat-card-blue">
          <div class="stat-icon">
            <i class="bi bi-clipboard-check"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ statistics.totalTests }}</div>
            <div class="stat-label">Total Tests</div>
          </div>
        </div>

        <div class="stat-card stat-card-green">
          <div class="stat-icon">
            <i class="bi bi-question-circle"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ statistics.totalQuestions }}</div>
            <div class="stat-label">Total Questions</div>
          </div>
        </div>

        <div class="stat-card stat-card-purple">
          <div class="stat-icon">
            <i class="bi bi-bookmark"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ statistics.totalSubjects }}</div>
            <div class="stat-label">Subjects</div>
          </div>
        </div>

        <div class="stat-card stat-card-orange">
          <div class="stat-icon">
            <i class="bi bi-star"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ statistics.mostUsedInteraction.type }}</div>
            <div class="stat-label">Most Used Interaction</div>
            <div class="stat-sublabel">({{ statistics.mostUsedInteraction.count }} uses)</div>
          </div>
        </div>
      </div>

      <!-- Section modifiée avec la logique de mode d'édition vs création -->
      <div class="row">
        <div id="EditorAnchor" style="height: 1px;"></div>
        <div v-if="showTestForm" >
          <!-- Conditionnellement afficher le formulaire de création ou d'édition -->
          <TestCreatorForm v-if="!isEditMode" @close="handleCloseTestCreator" @add-test="handleAddTest" />
          
        </div>
      </div>
      
      <div class="row allContainer">
        <div class="d-flex">
          <TestList @edit-test="editTest" ref="testList" />
        </div>
      </div>

      <div class="row allContainer" id="EditorMain" ref="editorMain">
        <TestInfo v-if="checkTest()" @close-editor="closeEditor" />
        <div class="noTestMessage" v-else><i>Please select a test in the list to edit it.</i></div>
      </div>

      <div class="row">
        <TabSystem v-if="checkTest()" :test-id="currentTestID" />
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref,computed,watch,nextTick,onMounted  } from 'vue';
import { useTestStore } from '../../stores/testStore';
import PageHeader from '../common/PageHeader.vue';
import FileUploader from '../../components/FileUploader.vue'
import api from '../../services/axios';

import TestList from '../../components/TestList.vue';
import TestInfo from '../../components/TestInfo.vue';
import TestCreatorForm from '../../components/TestCreatorForm.vue';
import TabSystem from '../../components/TestTabSystem.vue';


// State variables
const showTestForm = ref(false);
const showImportTest = ref(false);
const testList = ref(null);
const currentPreviewElement = ref(null); // L'élément affiché dans PreviewSection
const currentEditorElement = ref(null); // L'élément affiché dans ElementEditor
 // Afficher les outils de test

// Store
const store = useTestStore(); // Utilisation du store Pinia
const editorMain = ref(null);

// Statistics data
const statistics = ref({
  totalTests: 0,
  totalQuestions: 0,
  totalSubjects: 0,
  mostUsedInteraction: {
    type: 'N/A',
    count: 0
  }
});

// Fetch statistics
const fetchStatistics = async () => {
  try {
    const response = await api.get('/tests/statistics');
    statistics.value = response.data;
  } catch (error) {
    console.error('Error fetching statistics:', error);
  }
};

// Load statistics on mount
onMounted(() => {
  fetchStatistics();
});

// Ajouter des données JSON au tableau
const importTestToTable = (importedData) => {
  console.log("IMPORT TEST TO TABLE ")
  if (testList.value) {
    if (Array.isArray(importedData)) {
      importedData.forEach((test) => testList.value.addTest(test));
    } else {
      testList.value.addTest(importedData);
    }
  }
};

const isEditMode = ref(false);
// Methods to toggle components
const toggleTestForm = () => {
   isEditMode.value = false; // Mode création
  showTestForm.value = true;
  showImportTest.value = false;
   
};

const checkTest = () => {
  return !!store.testData?.ID; 
};

/* const toggleImportTest = () => {
  showImportTest.value = true;
  showTestForm.value = false;
};

const closeForm = () => {
  showTestForm.value = false;
}; */

const closeImportForm = () => {
  showImportTest.value = false;
};

const currentTestID = computed(() => store.testData?.ID || null);

const scrollToEditor = () => {
  // Utiliser setTimeout pour s'assurer que le DOM est complètement mis à jour
  setTimeout(() => {
    const editorElement = document.getElementById("EditorMain");
    if (editorElement) {
      // Calculer la position exacte
      const headerOffset = 20; // Ajustez si nécessaire (marge au-dessus)
      const elementPosition = editorElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      // Forcer le scroll
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      
      console.log("Scroll forcé à position:", offsetPosition);
    } else {
      console.error("Élément EditorMain introuvable dans le DOM");
    }
  }, 100); // Petit délai pour s'assurer que le DOM est mis à jour
};


// Handle editing a test
const editTest = (testData) => {
  // Les données sont déjà mises à jour dans TestList.vue via setTestData
  // On n'a pas besoin de les redéfinir ici
  clearPreviewAndEditor();
  showImportTest.value = false;
  isEditMode.value = true; // Mode édition
  
  // Ne pas afficher le formulaire de création pour les tests adaptatifs
  // La logique d'affichage sera gérée par TabSystem
  if (testData.progression !== 'adaptive') {
    showTestForm.value = true;
  } else {
    showTestForm.value = false;
  }
  
  nextTick(() => {
    scrollToEditor();
  });
};

const handleCloseTestCreator = () => {
  // Désactive le mode création
  showTestForm.value = false;
  //newTestData = {}; // Réinitialise les données du nouveau test
};

// Fonction pour ajouter un test dans TestList
const handleAddTest = (newTest) => {
  console.log("Test ajouté :", newTest);

  // Vérifiez si testList est défini et appelez addTest
  if (testList.value?.addTest) {
    testList.value.addTest(newTest);
  } else {
    console.error("testList n'est pas défini ou addTest n'est pas disponible.");
  }
};

const clearPreviewAndEditor = () => {
  currentPreviewElement.value = null;
  currentEditorElement.value = null;
  console.log("Preview et éditeur réinitialisés");
};


const handleUpdateTest = (updatedTest) => {
  console.log("Test mis à jour :", updatedTest);

  // Mettre à jour dans le store et la table
  store.updateCurrentTest(updatedTest);

  // Mettre à jour dans la liste
  if (testList.value?.updateTest) {
    testList.value.updateTest(updatedTest);
  } else {
    console.error("testList n'est pas défini ou updateTest n'est pas disponible.");
  }

  // Fermer le formulaire d'édition
  showTestForm.value = false;
};

// Fonction pour fermer l'éditeur et désélectionner la ligne
const closeEditor = () => {
  // Réinitialiser le store
  store.resetState();

  // Demander à TestList de désélectionner la ligne
  if (testList.value?.clearSelection) {
    testList.value.clearSelection();
  }

  // Fermer le formulaire de test si ouvert
  showTestForm.value = false;
  isEditMode.value = false;

  console.log("Éditeur fermé et sélection effacée");
};
</script>



<style>
/* Override some Bootstrap styles */

.EditorContainer {
  margin-top: 10px;
}

.form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(66, 184, 131, 0.25);
  border-color: #42b883;
}

.btn-primary {
  background-color: #42b883;
  border-color: #42b883;
}

.btn-primary:hover {
  background-color: #3aa876;
  border-color: #3aa876;
}

/* Custom styles */
.app-wrapper {
    background-color: #f8f9fa;
    border-left:1px solid #e9ecef;
}

main {
  padding:20px;
  background-color: white;
}

.ActionTest-btn {
  background-color: #426c9c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  margin-bottom: 10px;
  margin-left: 2px;
  cursor: pointer !important;
  width: 100px !important;
}

.ActionTest-btn:hover {
  background-color: #2e4c6d;
}

.allContainer {
  margin-top: 5px!important;
  ;
  border: 1px gainsboro solid;
  border-radius: 5px;
  padding: 10px;

}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.Importer {
  padding-top: 4px;
  height: 38px;
  text-align: left;
  border: 3px solid #2e4c6d;
  border-radius: 5px;

}

.previewContainer {
  margin-bottom: 10px;
}

.inputMainPrompt {
  border: gainsboro 1px solid;
  border-radius: 5px;
  width: 100%;
  padding: 10px;
}

.ml-2 {
  margin-left: 10px;
  ;
}

span.gap {
  background-color: yellow;
}

.gap-input{
  margin: 5px!important;
}

.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
}

.PositiveFeebackContainer {
  background-color: rgb(222, 246, 224);
  margin: 10px;
  padding: 10px;
  border: 1px green dotted;
  border-radius: 5px;
}

.NegativeFeebackContainer {
  background-color: rgb(246, 234, 235);
  margin: 10px;
  padding: 10px;
  border: 1px rgb(253, 97, 206) dotted;
  border-radius: 5px;
}

.btn-console {
  margin-left: 10px;
  color: white;
  background-color: #363F43;
}

.btn-console:hover {
  color: white;
  background-color: rgb(143, 74, 74)!important;
}

.ExportBT{
  
  margin-left: 3px;
  height : 35px;
  width: 200px;
}

.noTestMessage{
  padding : 30px;
 margin:0px 0px 0px  0px;
  background-color: #d5f5f6;
  border-radius: 5px;
}

.mainContainer{
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
}

h1{
  font-size: 2em;
}

.Tmanager{
  margin-left: 00px;
  margin-top: -10px;
  width: 100px!important;

}

/* Statistics Indicators */
.statistics-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin: 20px 0;
  padding: 0;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  flex-shrink: 0;
}

.stat-content {
  flex-grow: 1;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-sublabel {
  font-size: 0.75rem;
  opacity: 0.6;
  margin-top: 2px;
}

/* Blue theme */
.stat-card-blue {
  border-left-color: #6CB4EE;
}

.stat-card-blue .stat-icon {
  background-color: #E3F2FD;
  color: #1976D2;
}

.stat-card-blue .stat-value {
  color: #1565C0;
}

/* Green theme */
.stat-card-green {
  border-left-color: #77DD77;
}

.stat-card-green .stat-icon {
  background-color: #E8F5E9;
  color: #388E3C;
}

.stat-card-green .stat-value {
  color: #2E7D32;
}

/* Purple theme */
.stat-card-purple {
  border-left-color: #B19CD9;
}

.stat-card-purple .stat-icon {
  background-color: #F3E5F5;
  color: #7B1FA2;
}

.stat-card-purple .stat-value {
  color: #6A1B9A;
}

/* Orange theme */
.stat-card-orange {
  border-left-color: #FFB347;
}

.stat-card-orange .stat-icon {
  background-color: #FFF3E0;
  color: #E65100;
}

.stat-card-orange .stat-value {
  color: #D84315;
}

/* Responsive design */
@media (max-width: 768px) {
  .statistics-container {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 15px;
  }

  .stat-card {
    padding: 15px;
  }

  .stat-icon {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }

  .stat-value {
    font-size: 1.5rem;
  }
}
</style>

<style scoped>
/* Bouton New Test moderne */
.btn-new-test {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.btn-new-test i {
  font-size: 1.1rem;
}

.btn-new-test:hover {
  background: linear-gradient(135deg, #1565C0 0%, #0D47A1 100%);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  transform: translateY(-2px);
}

.btn-new-test:active {
  transform: translateY(0);
}
</style>